import * as React from 'react';

import { ZonedDateTime } from '@internationalized/date';
import {
  enumUnit,
  enumWorkOrderStatus,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { FormSection } from '@rematter/modules/settings/_components/_shared/FormSection';
import {
  Button,
  ConsoleDialog,
  TextArea,
  Select,
  Switch,
  DatePicker,
  NumberField,
  RadioGroupField,
  Radio,
  Grid,
  Row,
  Col,
  HDivider,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { Calculator, Check, Plus, Trash2 } from 'lucide-react';

import { SelectProcessingType } from '../../_components/select-fields/SelectProcessingType';
import { createWorkOrderAtom } from '../../_graphql/mutationWorkOrder';
import { $remoteAllProcessingTypes } from '../../_state/processingTypes.state';
import { useTranslations } from '../../i18n/useTranslations';
import {
  calculateFromInput,
  calculateFromOutput,
  hasRecipe,
  type CalculationDirection,
} from '../../utils/recipeCalculation';

import {
  WorkOrderInputItemRow,
  type WorkOrderInputItemData,
} from './WorkOrderInputItemRow';
import {
  WorkOrderOutputItemRow,
  type WorkOrderOutputItemData,
} from './WorkOrderOutputItemRow';

import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import {
  SelectInboundTollingAgreement,
  SelectableInboundTollingAgreement,
} from '~/shared/components/select-fields/SelectInboundTollingAgreement';
import {
  SelectInventoryLot,
  SelectableInventoryLot,
} from '~/shared/components/select-fields/SelectInventoryLot';
import { useIdentity } from '~/shared/hooks';

type WorkOrderDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

let inputItemIdCounter = 0;
let outputItemIdCounter = 0;

const createEmptyInputItem = (): WorkOrderInputItemData => ({
  id: `new-input-${++inputItemIdCounter}`,
  itemId: '',
  plannedQuantityUnit: enumUnit.Pounds,
  plannedQuantity: '',
  notes: '',
});

const createEmptyOutputItem = (): WorkOrderOutputItemData => ({
  id: `new-output-${++outputItemIdCounter}`,
  itemId: '',
  expectedQuantityUnit: enumUnit.Pounds,
  expectedQuantity: '',
  notes: '',
});

export const WorkOrderDialog = ({
  isOpen,
  onClose,
  onSuccess,
}: WorkOrderDialogProps) => {
  const t = useTranslations();
  const toast = useToast();
  const { user } = useIdentity();

  const [processingTypeId, setProcessingTypeId] = React.useState('');
  const [facilityId, setFacilityId] = React.useState('');
  const [scheduledStartDateTime, setScheduledStartDateTime] =
    React.useState<ZonedDateTime | null>(null);
  const [scheduledEndDateTime, setScheduledEndDateTime] =
    React.useState<ZonedDateTime | null>(null);
  const [notes, setNotes] = React.useState('');
  const [isTollProcessing, setIsTollProcessing] = React.useState(false);
  const [inboundTollingAgreementId, setInboundTollingAgreementId] =
    React.useState<string | undefined>(undefined);
  const [tollCustomerAccountId, setTollCustomerAccountId] = React.useState<
    string | undefined
  >(undefined);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [sourceLotId, setSourceLotId] = React.useState<string | undefined>(
    undefined,
  );
  const [inputItems, setInputItems] = React.useState<WorkOrderInputItemData[]>(
    [],
  );
  const [outputItems, setOutputItems] = React.useState<
    WorkOrderOutputItemData[]
  >([]);

  const [calculationDirection, setCalculationDirection] =
    React.useState<CalculationDirection>('FromInputs');
  const [referenceItemId, setReferenceItemId] = React.useState('');
  const [referenceQuantity, setReferenceQuantity] = React.useState<
    number | null
  >(null);
  const [referenceUnit, setReferenceUnit] = React.useState<Unit>(
    enumUnit.Pounds,
  );

  const { data: processingTypesData } = useAtomValue($remoteAllProcessingTypes);
  const processingTypes = React.useMemo(
    () => processingTypesData?.processingTypes ?? [],
    [processingTypesData?.processingTypes],
  );

  const selectedProcessingType = React.useMemo(
    () => processingTypes.find((pt) => pt.id === processingTypeId),
    [processingTypes, processingTypeId],
  );

  const processingTypeHasRecipe = React.useMemo(
    () =>
      hasRecipe(
        selectedProcessingType?.inputItems,
        selectedProcessingType?.outputItems,
      ),
    [selectedProcessingType],
  );

  const referenceItemOptions = React.useMemo(() => {
    if (!selectedProcessingType) return [];

    if (calculationDirection === 'FromInputs') {
      return (
        selectedProcessingType.inputItems?.map((item) => ({
          key: item.itemId,
          label: item.itemId,
        })) ?? []
      );
    } else {
      return (
        selectedProcessingType.outputItems?.map((item) => ({
          key: item.itemId,
          label: `${item.itemId} (${item.yieldPercentage}% ${item.outputUnit})`,
        })) ?? []
      );
    }
  }, [selectedProcessingType, calculationDirection]);

  const createWorkOrder = useSetAtom(createWorkOrderAtom({}));

  const handleClose = React.useCallback(() => {
    setProcessingTypeId('');
    setFacilityId('');
    setScheduledStartDateTime(null);
    setScheduledEndDateTime(null);
    setNotes('');
    setIsTollProcessing(false);
    setInboundTollingAgreementId(undefined);
    setTollCustomerAccountId(undefined);
    setSourceLotId(undefined);
    setInputItems([]);
    setOutputItems([]);
    setCalculationDirection('FromInputs');
    setReferenceItemId('');
    setReferenceQuantity(null);
    setReferenceUnit(enumUnit.Pounds);
    onClose();
  }, [onClose]);

  const handleAddInputItem = React.useCallback(() => {
    setInputItems((prev) => [...prev, createEmptyInputItem()]);
  }, []);

  const handleUpdateInputItem = React.useCallback(
    (id: string, data: Partial<WorkOrderInputItemData>) => {
      setInputItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...data } : item)),
      );
    },
    [],
  );

  const handleRemoveInputItem = React.useCallback((id: string) => {
    setInputItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleAddOutputItem = React.useCallback(() => {
    setOutputItems((prev) => [...prev, createEmptyOutputItem()]);
  }, []);

  const handleUpdateOutputItem = React.useCallback(
    (id: string, data: Partial<WorkOrderOutputItemData>) => {
      setOutputItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...data } : item)),
      );
    },
    [],
  );

  const handleRemoveOutputItem = React.useCallback((id: string) => {
    setOutputItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleInboundTollingAgreementChange = React.useCallback(
    (
      agreementId: string | undefined,
      agreement?: SelectableInboundTollingAgreement,
    ) => {
      setInboundTollingAgreementId(agreementId);
      setTollCustomerAccountId(agreement?.customerAccount?.id);
    },
    [],
  );

  const handleSourceLotChange = React.useCallback(
    (lotId: string | undefined, lot?: SelectableInventoryLot) => {
      setSourceLotId(lotId);

      if (lot && lot.quantitySummary && lot.quantitySummary.length > 0) {
        const newInputItems: WorkOrderInputItemData[] = lot.quantitySummary.map(
          (summary) => ({
            id: `lot-input-${++inputItemIdCounter}`,
            itemId: summary.itemId,
            lotId: lot.id,
            plannedQuantityUnit: summary.quantity.unit as Unit,
            plannedQuantity: String(summary.quantity.value),
            notes: '',
          }),
        );
        setInputItems(newInputItems);
      } else if (!lotId) {
        setInputItems([]);
      }
    },
    [],
  );

  const handleApplyRecipe = React.useCallback(() => {
    if (
      !selectedProcessingType ||
      !referenceItemId ||
      referenceQuantity === null ||
      referenceQuantity <= 0
    ) {
      return;
    }

    const recipeInputs = selectedProcessingType.inputItems ?? [];
    const recipeOutputs = selectedProcessingType.outputItems ?? [];

    const result =
      calculationDirection === 'FromInputs'
        ? calculateFromInput(
            recipeInputs,
            recipeOutputs,
            referenceQuantity,
            referenceUnit,
          )
        : calculateFromOutput(
            recipeInputs,
            recipeOutputs,
            referenceItemId,
            referenceQuantity,
            referenceUnit,
          );

    if (!result) {
      toast.error('Could not calculate recipe');
      return;
    }

    setInputItems(
      result.inputs.map((input) => ({
        id: `recipe-input-${++inputItemIdCounter}`,
        itemId: input.itemId,
        plannedQuantityUnit: input.quantityUnit,
        plannedQuantity: input.quantity.toFixed(2),
        notes: '',
      })),
    );

    setOutputItems(
      result.outputs.map((output) => ({
        id: `recipe-output-${++outputItemIdCounter}`,
        itemId: output.itemId,
        expectedQuantityUnit: output.quantityUnit,
        expectedQuantity: output.quantity.toFixed(2),
        notes: '',
      })),
    );

    toast.success(t.workOrdersView.dialog.calculation.applySuccess);
  }, [
    selectedProcessingType,
    referenceItemId,
    referenceQuantity,
    referenceUnit,
    calculationDirection,
    toast,
    t,
  ]);

  const handleClearItems = React.useCallback(() => {
    setInputItems([]);
    setOutputItems([]);
    setReferenceItemId('');
    setReferenceQuantity(null);
  }, []);

  const handleSubmit = React.useCallback(async () => {
    if (!processingTypeId) {
      toast.error(t.workOrdersView.dialog.validation.processingTypeRequired);
      return;
    }

    if (!facilityId) {
      toast.error(t.workOrdersView.dialog.validation.facilityRequired);
      return;
    }

    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    // Validate input items
    const validInputItems = inputItems.filter(
      (item) => item.itemId && item.plannedQuantity,
    );
    for (const item of validInputItems) {
      if (!item.itemId) {
        toast.error(t.workOrdersView.dialog.validation.inputItemRequired);
        return;
      }
      if (!item.plannedQuantity || parseFloat(item.plannedQuantity) <= 0) {
        toast.error(t.workOrdersView.dialog.validation.quantityRequired);
        return;
      }
    }

    // Validate output items
    const validOutputItems = outputItems.filter(
      (item) => item.itemId && item.expectedQuantity,
    );
    for (const item of validOutputItems) {
      if (!item.itemId) {
        toast.error(t.workOrdersView.dialog.validation.outputItemRequired);
        return;
      }
      if (!item.expectedQuantity || parseFloat(item.expectedQuantity) <= 0) {
        toast.error(t.workOrdersView.dialog.validation.quantityRequired);
        return;
      }
    }

    try {
      setIsSubmitting(true);

      await createWorkOrder({
        processingTypeId,
        facilityId,
        status: enumWorkOrderStatus.Draft,
        tollingType: isTollProcessing ? 'Inbound' : 'None',
        inboundTollingAgreementId: isTollProcessing
          ? inboundTollingAgreementId
          : undefined,
        tollCustomerAccountId: isTollProcessing
          ? tollCustomerAccountId
          : undefined,
        scheduledStartDate: scheduledStartDateTime?.toAbsoluteString(),
        scheduledEndDate: scheduledEndDateTime?.toAbsoluteString(),
        notes: notes.trim() || undefined,
        inputItems: validInputItems.map((item) => ({
          itemId: item.itemId,
          lotId: item.lotId || undefined,
          plannedQuantityUnit: item.plannedQuantityUnit,
          plannedQuantity: parseFloat(item.plannedQuantity),
          notes: item.notes || undefined,
        })),
        outputItems: validOutputItems.map((item) => ({
          itemId: item.itemId,
          targetLotId: item.targetLotId || undefined,
          expectedQuantityUnit: item.expectedQuantityUnit,
          expectedQuantity: parseFloat(item.expectedQuantity),
          isNoCost: false,
          notes: item.notes || undefined,
        })),
      });

      toast.success(t.workOrdersView.dialog.createSuccess);
      onSuccess?.();
      handleClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred';
      toast.error(t.workOrdersView.dialog.createError(errorMessage));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    processingTypeId,
    facilityId,
    scheduledStartDateTime,
    scheduledEndDateTime,
    notes,
    isTollProcessing,
    inboundTollingAgreementId,
    tollCustomerAccountId,
    inputItems,
    outputItems,
    user,
    createWorkOrder,
    toast,
    t,
    onSuccess,
    handleClose,
  ]);

  const canSubmit =
    Boolean(processingTypeId) && Boolean(facilityId) && !isSubmitting;

  return (
    <ConsoleDialog
      className="@WorkOrderDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={t.workOrdersView.dialog.createTitle}
      content={
        <Col gap6 className="min-w-[700px]">
          {/* Basic Info */}
          <Col gap4>
            <SelectProcessingType
              label={t.workOrdersView.dialog.processingTypeLabel}
              value={processingTypeId || undefined}
              onChange={(value) => setProcessingTypeId(value ?? '')}
              placeholder={t.workOrdersView.dialog.processingTypePlaceholder}
              isRequired
            />

            <SelectFacility
              label={t.workOrdersView.dialog.facilityLabel}
              value={facilityId || undefined}
              onChange={(value) => setFacilityId(value ?? '')}
              placeholder={t.workOrdersView.dialog.facilityPlaceholder}
              isRequired
            />

            <Grid cols2 gap4>
              <DatePicker
                label={t.workOrdersView.dialog.scheduledStartLabel}
                value={scheduledStartDateTime}
                onChange={(date) =>
                  setScheduledStartDateTime(date as ZonedDateTime | null)
                }
                granularity="minute"
              />
              <DatePicker
                label={t.workOrdersView.dialog.scheduledEndLabel}
                value={scheduledEndDateTime}
                onChange={(date) =>
                  setScheduledEndDateTime(date as ZonedDateTime | null)
                }
                granularity="minute"
              />
            </Grid>

            <TextArea
              label={t.workOrdersView.dialog.notesLabel}
              value={notes}
              onChange={setNotes}
              placeholder={t.workOrdersView.dialog.notesPlaceholder}
            />

            <Row gap2>
              <Switch
                isSelected={isTollProcessing}
                onChange={setIsTollProcessing}
              />
              <span className="text-sm">
                {t.workOrdersView.dialog.isTollingLabel}
              </span>
            </Row>

            {isTollProcessing && (
              <SelectInboundTollingAgreement
                label={t.workOrdersView.dialog.tollAgreementLabel}
                value={inboundTollingAgreementId}
                onChange={handleInboundTollingAgreementChange}
                placeholder={t.workOrdersView.dialog.tollAgreementPlaceholder}
              />
            )}
          </Col>

          {/* Recipe Calculation */}
          {processingTypeId && (
            <div className="rounded-lg border border-neutral/20 bg-neutral/5 p-4">
              <div className="mb-3 flex items-center gap-2">
                <Calculator size={18} className="text-primary" />
                <h3 className="font-medium">
                  {t.workOrdersView.dialog.calculation.title}
                </h3>
              </div>

              {!processingTypeHasRecipe ? (
                <p className="text-muted-foreground text-sm">
                  {t.workOrdersView.dialog.calculation.noRecipe}
                </p>
              ) : (
                <div className="space-y-4">
                  <p className="text-muted-foreground text-sm">
                    {t.workOrdersView.dialog.calculation.hasRecipe}
                  </p>

                  <RadioGroupField
                    label={t.workOrdersView.dialog.calculation.direction}
                    value={calculationDirection}
                    onChange={(value) =>
                      setCalculationDirection(value as CalculationDirection)
                    }
                    orientation="horizontal"
                  >
                    <Radio value="FromInputs">
                      {t.workOrdersView.dialog.calculation.fromInputs}
                    </Radio>
                    <Radio value="FromOutputs">
                      {t.workOrdersView.dialog.calculation.fromOutputs}
                    </Radio>
                  </RadioGroupField>

                  <div className="grid grid-cols-3 gap-3">
                    <Select
                      label={t.workOrdersView.dialog.calculation.referenceItem}
                      value={referenceItemId}
                      onChange={(key) => setReferenceItemId(String(key))}
                      options={referenceItemOptions}
                      placeholder={t.itemFields.selectItem}
                    />
                    <NumberField
                      label={
                        t.workOrdersView.dialog.calculation.referenceQuantity
                      }
                      value={referenceQuantity ?? undefined}
                      onChange={(val) => setReferenceQuantity(val ?? null)}
                      minValue={0}
                    />
                    <Select
                      label={t.workOrdersView.dialog.calculation.referenceUnit}
                      value={referenceUnit}
                      onChange={(key) => setReferenceUnit(key as Unit)}
                      options={[
                        { key: enumUnit.Pounds, label: t.units.lb },
                        { key: enumUnit.Kilograms, label: t.units.kg },
                        { key: enumUnit.MetricTons, label: t.units.mt },
                        { key: enumUnit.ShortTons, label: t.units.st },
                        { key: enumUnit.Each, label: t.units.ea },
                      ]}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="solid"
                      color="primary"
                      size="small"
                      icon={<Calculator size={14} />}
                      onPress={handleApplyRecipe}
                      isDisabled={
                        !referenceItemId ||
                        referenceQuantity === null ||
                        referenceQuantity <= 0
                      }
                    >
                      {t.workOrdersView.dialog.calculation.apply}
                    </Button>
                    {(inputItems.length > 0 || outputItems.length > 0) && (
                      <Button
                        variant="outline"
                        color="neutral"
                        size="small"
                        icon={<Trash2 size={14} />}
                        onPress={handleClearItems}
                      >
                        {t.workOrdersView.dialog.calculation.clear}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Source Lot Selection */}
          {facilityId && (
            <div className="space-y-3">
              <div className="rounded-lg border border-neutral/20 bg-neutral/5 p-4">
                <h3 className="mb-3 font-medium">
                  {t.workOrdersView.dialog.sourceLot.title}
                </h3>
                <p className="mb-3 text-sm text-neutral/60">
                  {t.workOrdersView.dialog.sourceLot.description}
                </p>
                <SelectInventoryLot
                  label={t.workOrdersView.dialog.sourceLot.selectLabel}
                  placeholder={
                    t.workOrdersView.dialog.sourceLot.selectPlaceholder
                  }
                  value={sourceLotId}
                  onChange={handleSourceLotChange}
                  facilityId={facilityId}
                  isDisabled={isSubmitting}
                />
              </div>
            </div>
          )}

          {/* Input Items */}
          <HDivider />
          <FormSection
            title={
              <Row xBetween className={'w-full'}>
                <span>{t.workOrdersView.dialog.inputItems.title}</span>

                {!sourceLotId && (
                  <Button
                    variant="outline"
                    color="neutral"
                    size="small"
                    icon={<Plus size={16} />}
                    onPress={handleAddInputItem}
                  >
                    {t.workOrdersView.dialog.inputItems.add}
                  </Button>
                )}
              </Row>
            }
          >
            {inputItems.length === 0 ? (
              <p className="py-4 text-center text-sm text-neutral/60">
                {sourceLotId
                  ? t.workOrdersView.dialog.inputItems.emptyLot
                  : t.workOrdersView.dialog.inputItems.empty}
              </p>
            ) : (
              <div className="space-y-2">
                {inputItems.map((item) => (
                  <WorkOrderInputItemRow
                    key={item.id}
                    item={item}
                    onUpdate={handleUpdateInputItem}
                    onRemove={handleRemoveInputItem}
                    isDisabled={isSubmitting || Boolean(sourceLotId)}
                    facilityId={facilityId || undefined}
                    showLotSelector={false}
                  />
                ))}
              </div>
            )}
          </FormSection>

          <HDivider />

          {/* Output Items */}
          <FormSection
            title={
              <Row xBetween className={'w-full'}>
                <span>{t.workOrdersView.dialog.outputItems.title}</span>
                <Button
                  variant="outline"
                  color="neutral"
                  size="small"
                  icon={<Plus size={16} />}
                  onPress={handleAddOutputItem}
                >
                  {t.workOrdersView.dialog.outputItems.add}
                </Button>
              </Row>
            }
          >
            {outputItems.length === 0 ? (
              <p className="py-4 text-center text-sm text-neutral/60">
                {t.workOrdersView.dialog.outputItems.empty}
              </p>
            ) : (
              <Col gap2>
                {outputItems.map((item) => (
                  <WorkOrderOutputItemRow
                    key={item.id}
                    item={item}
                    onUpdate={handleUpdateOutputItem}
                    onRemove={handleRemoveOutputItem}
                    isDisabled={isSubmitting}
                    facilityId={facilityId || undefined}
                    showLotSelector={Boolean(facilityId)}
                  />
                ))}
              </Col>
            )}
          </FormSection>
        </Col>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.workOrdersView.dialog.cancel}
          </Button>
          <Button
            icon={<Check />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {isSubmitting
              ? t.workOrdersView.dialog.creating
              : t.workOrdersView.dialog.create}
          </Button>
        </div>
      }
    />
  );
};
